import io, { Socket } from "socket.io-client";
import axios, { AxiosInstance } from "axios";
import type { DetectEventPayload, ClusterUpdate } from "@/types";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
  EventDetectedPayload,
  NotificationPayload,
  WsErrorPayload,
} from "@/types/geolocation";
import { env } from "@/config/env";

export interface CreateEventResponse {
  success: boolean;
  eventId: string;
  clusterId: string | null;
  message: string;
}

export class ApiClient {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  private httpClient: AxiosInstance;
  private httpUrl: string;
  private wsUrl: string;
  private connected = false;
  private userId: string | null = null;
  /** Monotonic counter for the /time cache-buster (paired with Date.now() for uniqueness). */
  private timeNonce = 0;

  constructor(serverUrl?: string) {
    const baseUrl = serverUrl || env.apiUrl.replace('/api', '');
    this.httpUrl = baseUrl;
    this.wsUrl = baseUrl;
    this.httpClient = axios.create({
      baseURL: `${serverUrl}/api`,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  connect(userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userId = userId;

      try {
        this.socket = io(`${this.wsUrl}/events`, {
          auth: { userId },
          transports: ["websocket"],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 5,
        });

        this.socket.on("connect", () => {
          this.connected = true;
          resolve();
        });

        this.socket.on("disconnect", () => {
          this.connected = false;
        });

        this.socket.on("connect_error", (error) => {
          console.error("WebSocket connection error:", error);
          reject(new Error("Не удалось подключиться к серверу"));
        });

        this.socket.on("error", (error) => {
          console.error("WebSocket error:", error);
        });
      } catch (error) {
        reject(new Error("Не удалось создать WebSocket соединение"));
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.userId = null;
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Lightweight server-time probe for clock synchronisation (Cristian's algorithm). Returns the
   * server's epoch-ms at the moment it handled the request; the caller brackets this call with
   * its own Date.now() to estimate clock offset and RTT. Kept dependency-free and DB-less.
   */
  getServerTime(): Promise<number> {
    // Cache-buster: unique value per call so no browser/proxy cache can serve a stale
    // timestamp (which would masquerade as a clock offset). Server already sends
    // Cache-Control: no-store, but the query param guarantees a fresh round-trip.
    // The nginx `location = /api/time` matches by path only, so the query is ignored there.
    const nonce = `${Date.now()}-${(this.timeNonce = (this.timeNonce + 1) >>> 0)}`;
    // Short timeout: a dead network must not stall the event POST that awaits clock-sync.
    return this.httpClient
      .get<{ t: number }>("/time", { params: { _: nonce }, timeout: 3000 })
      .then((response) => response.data.t);
  }

  sendEvent(payload: DetectEventPayload): Promise<CreateEventResponse> {
    return this.httpClient.post<CreateEventResponse>("/events", payload).then(
      (response) => response.data,
      (error) => {
        throw new Error(
          error.response?.data?.message || "Ошибка отправки события",
        );
      },
    );
  }

  /**
   * Upload a detection audio clip (as WAV) + the full "how it was detected" report to the server
   * for analysis — manual user action from the My-Events card. Sent as multipart/form-data so the
   * binary WAV goes efficiently alongside a small JSON metadata blob and the structured report
   * (no base64 inflation). The default Content-Type is left untouched so the browser sets the
   * multipart boundary itself.
   *
   * `meta` carries the scalar lookup fields (timestamp / device / class / confidence); `report`
   * is the complete reasoning trace (cascade stages, class probabilities, all diagnostics, audio
   * meta, embedding) built by utils/detectionReport.ts — optional so older callers still work.
   *
   * NOTE: this is the only path that sends captured microphone audio off-device — every other
   * audio flow (auto-capture, listen-back) stays local. It exists exclusively for this button.
   */
  uploadAudioSample(
    wav: Blob,
    meta: {
      timestamp: number;
      deviceId: string;
      userId?: string;
      className: string;
      confidence: number;
      report?: unknown;
    },
  ): Promise<{ success: boolean; sampleId: string }> {
    const form = new FormData();
    form.append("audio", wav, `clip-${meta.timestamp}.wav`);
    form.append("meta", JSON.stringify(meta));
    if (meta.report !== undefined) {
      // Structured reasoning trace travels as its own JSON field so the server can persist and
      // query it without re-parsing the scalar meta.
      form.append("report", JSON.stringify(meta.report));
    }
    return this.httpClient
      .post<{ success: boolean; sampleId: string }>("/audio-samples", form, {
        timeout: 20000, // audio ~150 KB, but give headroom on slow links
        // Do NOT set Content-Type manually: axios lets the browser set the multipart
        // boundary itself for FormData (an explicit header would drop the boundary and
        // break the server's multipart parse).
        headers: { "Content-Type": undefined },
      })
      .then(
        (response) => response.data,
        (error) => {
          throw new Error(
            error.response?.data?.message || "Ошибка отправки аудио",
          );
        },
      );
  }

  async getClusters(
    minutes: number = 20,
    limit: number = 50,
  ): Promise<{
    success: boolean;
    data: ClusterUpdate[];
    count: number;
  }> {
    try {
      const response = await this.httpClient.get<{
        success: boolean;
        data: ClusterUpdate[];
        count: number;
      }>("/events/clusters", {
        params: { minutes, limit },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch clusters:", error);
      return { success: false, data: [], count: 0 };
    }
  }

  onClusterUpdate(callback: (cluster: ClusterUpdate) => void): void {
    if (!this.socket) return;

    this.socket.on("cluster-update", (payload) => {
      callback(payload as unknown as ClusterUpdate);
    });
  }

  onEventDetected(callback: (event: EventDetectedPayload) => void): void {
    if (!this.socket) return;

    this.socket.on("event-detected", callback);
  }

  onNotification(callback: (notification: NotificationPayload) => void): void {
    if (!this.socket) return;

    this.socket.on("notification", callback);
  }

  onConnect(callback: () => void): void {
    if (!this.socket) return;

    this.socket.on("connect", callback);
  }

  onDisconnect(callback: () => void): void {
    if (!this.socket) return;

    this.socket.on("disconnect", callback);
  }

  onError(callback: (error: WsErrorPayload) => void): void {
    if (!this.socket) return;

    this.socket.on("error", callback);
  }

  removeListener(event: string, callback: (...args: unknown[]) => void): void {
    if (!this.socket) return;

    this.socket.off(event as keyof ServerToClientEvents, callback as never);
  }

  removeAllListeners(): void {
    if (!this.socket) return;

    this.socket.removeAllListeners();
  }
}
